import { LocalStorageConsts } from '@/constants/localstorage';
import { DeviceType } from '@/types/navigator.type';

/**
 * The camera the user last used, remembered across reloads.
 *
 * Both the id and the label are stored. A deviceId is stable only for as long
 * as the site keeps its camera permission — browsers reissue it once site data
 * is cleared — whereas the label survives that, so it gives a second chance at
 * recognising the same camera before falling back to the default one.
 */
export const readStoredVideoDevice = (): DeviceType | null => {
  try {
    const raw = localStorage.getItem(LocalStorageConsts.VIDEO_DEVICE);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (typeof parsed?.id !== 'string' || parsed.id === '') {
      return null;
    }

    return { id: parsed.id, label: typeof parsed.label === 'string' ? parsed.label : '' };
  } catch (error) {
    // A malformed entry should cost the user their camera preference, nothing
    // more — fall back to the default rather than breaking stream setup.
    console.error('Could not read the remembered camera', error);
    return null;
  }
};

export const writeStoredVideoDevice = (device: DeviceType | null): void => {
  try {
    if (!device?.id) {
      localStorage.removeItem(LocalStorageConsts.VIDEO_DEVICE);
      return;
    }

    localStorage.setItem(
      LocalStorageConsts.VIDEO_DEVICE,
      JSON.stringify({ id: device.id, label: device.label }),
    );
  } catch (error) {
    console.error('Could not remember the camera', error);
  }
};

/**
 * The remembered camera, but only when it is still among the cameras this
 * machine currently offers. A stale id must never be returned: it reaches
 * getUserMedia as an `exact` deviceId constraint, which fails outright rather
 * than falling back to another camera.
 */
export const matchStoredVideoDevice = (available: DeviceType[]): DeviceType | undefined => {
  const stored = readStoredVideoDevice();
  if (!stored) {
    return undefined;
  }

  return (
    available.find((device) => device.id === stored.id) ??
    available.find((device) => !!stored.label && device.label === stored.label)
  );
};
